import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import StarIcon from "@mui/icons-material/Star";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import "./KotPlayerResultDialog.css";

function formatPlayerName(p) {
  if (!p) return "—";
  return (
    p.user_alias ||
    p.user_first_name ||
    p.user_last_name ||
    (p.player_id ? String(p.player_id).slice(-6) : "—")
  );
}

export function KotPlayerResultDialog({
  open,
  match,
  leaderboard = [],
  coach,
  formatPlayerLabel,
}) {
  const { t } = useTranslation();
  const [animateSuccess, setAnimateSuccess] = useState(false);

  // Clear animation state when reopened
  useEffect(() => {
    if (open) {
      setAnimateSuccess(false);
    }
  }, [open]);

  if (!open || !match || !coach) return null;

  const resolveName =
    formatPlayerLabel ||
    ((player, playerId) => {
      if (player) return formatPlayerName(player);
      if (playerId && Array.isArray(leaderboard)) {
        const row = leaderboard.find(
          (r) => String(r.player_id) === String(playerId)
        );
        if (row) return formatPlayerName(row);
      }
      if (playerId) return String(playerId).slice(-6);
      return "—";
    });

  const nameA = resolveName(match.player_a, match.player_a_id);
  const nameB = resolveName(match.player_b, match.player_b_id);
  
  const {
    wizardStep,
    winnerSide,
    loserScore,
    resolvedScores,
    goldenPointEnabled,
    submitting,
    error,
    pickWinner,
    adjustLoserScore,
    setGoldenPointEnabled,
    wizardBack,
    canSubmit,
    close,
    submit,
  } = coach;

  const { scoreA, scoreB } = resolvedScores;
  const winnerIsA = winnerSide === "A";
  const winnerIsB = winnerSide === "B";
  const winnerName = winnerIsA ? nameA : winnerIsB ? nameB : null;

  const errorText =
    error && (error.startsWith("kot") ? t(error) || error : error);

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setAnimateSuccess(true);
    // Add small delay for animation to play before actual submit
    setTimeout(() => {
      submit();
    }, 600);
  };

  return (
    <div
      className="kot-player-result__backdrop"
      role="presentation"
      onClick={() => !submitting && close()}
    >
      <div
        className={`kot-player-result__sheet ${animateSuccess ? 'kot-player-result__sheet--success' : ''}`}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="kot-player-result__close"
          onClick={close}
          disabled={submitting}
          aria-label={t("cancel")}
        >
          <CloseIcon fontSize="large" />
        </button>

        {animateSuccess ? (
          <div className="kot-player-result__success-overlay">
            <CheckCircleIcon className="kot-player-result__success-icon" />
            <h2 className="kot-player-result__success-text">{t("kotResultSubmitted") || "Result Saved!"}</h2>
          </div>
        ) : (
          <div className="kot-player-result__content">
            <div className="kot-player-result__header">
              <h2 className="kot-player-result__title">
                {t("table_with_number", {
                  number: match.table_number ?? "—",
                })}
              </h2>
              <div className="kot-player-result__progress">
                <div className={`kot-player-result__step ${wizardStep === "winner" ? "active" : "completed"}`} />
                <div className={`kot-player-result__step ${wizardStep === "scores" ? "active" : ""}`} />
              </div>
            </div>

            <div className="kot-player-result__body">
              {wizardStep === "winner" ? (
                <div className="kot-player-result__winner-step">
                  <h3 className="kot-player-result__instruction">
                    {t("kotWizardPickWinner") || "Who won?"}
                  </h3>
                  
                  <div className="kot-player-result__vs-container">
                    <button
                      className={`kot-player-result__card ${winnerIsA ? 'selected' : ''}`}
                      onClick={() => pickWinner("A")}
                    >
                      <div className="kot-player-result__card-icon">
                        {winnerIsA && <EmojiEventsIcon />}
                      </div>
                      <span className="kot-player-result__card-name">{nameA}</span>
                    </button>
                    
                    <div className="kot-player-result__vs-badge">VS</div>
                    
                    <button
                      className={`kot-player-result__card ${winnerIsB ? 'selected' : ''}`}
                      onClick={() => pickWinner("B")}
                    >
                      <div className="kot-player-result__card-icon">
                        {winnerIsB && <EmojiEventsIcon />}
                      </div>
                      <span className="kot-player-result__card-name">{nameB}</span>
                    </button>
                  </div>
                </div>
              ) : null}

              {wizardStep === "scores" ? (
                <div className="kot-player-result__score-step">
                  <h3 className="kot-player-result__instruction">
                    {t("kotWizardLoserScore", { winner: winnerName }) || `${winnerName}'s opponent scored:`}
                  </h3>
                  
                  <div className="kot-player-result__score-input">
                    <button
                      className="kot-player-result__score-btn minus"
                      onClick={() => adjustLoserScore(-1)}
                      aria-label="Decrease score"
                    >
                      <RemoveIcon />
                    </button>
                    
                    <div className="kot-player-result__score-display">
                      <div className="kot-player-result__score-value">{loserScore}</div>
                      <div className="kot-player-result__score-label">{t("points")}</div>
                    </div>
                    
                    <button
                      className="kot-player-result__score-btn plus"
                      onClick={() => adjustLoserScore(1)}
                      aria-label="Increase score"
                    >
                      <AddIcon />
                    </button>
                  </div>

                  <div className="kot-player-result__summary">
                    <div className={`kot-player-result__summary-player ${winnerIsA ? 'winner' : 'loser'}`}>
                      <span className="name">{nameA}</span>
                      <span className="score">{scoreA}</span>
                    </div>
                    <div className="kot-player-result__summary-divider">-</div>
                    <div className={`kot-player-result__summary-player ${winnerIsB ? 'winner' : 'loser'}`}>
                      <span className="score">{scoreB}</span>
                      <span className="name">{nameB}</span>
                    </div>
                  </div>

                  <button 
                    className={`kot-player-result__golden-point ${goldenPointEnabled ? 'active' : ''}`}
                    onClick={() => setGoldenPointEnabled(!goldenPointEnabled)}
                  >
                    {goldenPointEnabled ? <StarIcon /> : <StarOutlineIcon />}
                    <span>{t("kotGoldenPointCheckbox") || "Golden Point played"}</span>
                  </button>
                </div>
              ) : null}

              {errorText ? (
                <div className="kot-player-result__error" role="alert">
                  {errorText}
                </div>
              ) : null}
            </div>

            <div className="kot-player-result__footer">
              {wizardStep === "scores" && (
                <button
                  type="button"
                  className="kot-player-result__btn-back"
                  disabled={submitting}
                  onClick={wizardBack}
                >
                  {t("back") || "Back"}
                </button>
              )}
              
              {wizardStep === "scores" && (
                <button
                  type="button"
                  className="kot-player-result__btn-submit"
                  disabled={submitting || !canSubmit}
                  onClick={handleSubmit}
                >
                  {submitting ? t("submitting") : t("submit")}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default KotPlayerResultDialog;
